/**
 * "Create" and "Create and add another" — one form, two outcomes.
 *
 * A create form has two honest endings and they want different navigation:
 *
 * - **Create** → go to the thing you just made. That is where an operator
 *   verifies it and where the next action lives. Landing back on an empty
 *   form after a successful create leaves the operator to work out whether
 *   it worked, and a summary panel answering that question is a worse
 *   version of the detail page they actually wanted.
 * - **Create and add another** → stay, keep the shared context (the client,
 *   the cohort, the tier), clear only the per-person fields. This is the
 *   batch case: enrolling a wave one student at a time, where re-picking the
 *   client forty times is the whole cost.
 *
 * The intent is carried in a REF, not state. The primary is the form's only
 * `type="submit"`, so implicit Enter submission always takes the ordinary
 * create-and-open path. The secondary is a `type="button"` that records its
 * intent and calls `requestSubmit()` on the owning form; that preserves the
 * required Cancel → secondary → primary focus order without making the
 * secondary action the browser's default submit button.
 *
 * `useSubmitIntent` returns the ref plus the two button props, so a form
 * cannot wire one button and forget the other.
 */
import {
  useCallback,
  useRef,
  type MouseEvent,
  type ReactNode,
} from 'react'
import { PendingSpinner } from './save-button'
import { Button, type ButtonProps } from '@speaking-coach/ui'

export type SubmitIntent = 'close' | 'another'

export interface SubmitPair {
  /** Read this in the submit handler to decide where to go afterwards. */
  intent: React.MutableRefObject<SubmitIntent>
  /** Spread onto the primary button. */
  primaryProps: { type: 'submit'; onClick: () => void }
  /** Spread onto the secondary button. */
  anotherProps: {
    type: 'button'
    onClick: (event: MouseEvent<HTMLButtonElement>) => void
  }
}

export function useSubmitIntent(): SubmitPair {
  const intent = useRef<SubmitIntent>('close')
  const setClose = useCallback(() => {
    intent.current = 'close'
  }, [])
  const submitAnother = useCallback((event: MouseEvent<HTMLButtonElement>) => {
    intent.current = 'another'
    event.currentTarget.form?.requestSubmit()
  }, [])
  return {
    intent,
    primaryProps: { type: 'submit', onClick: setClose },
    anotherProps: { type: 'button', onClick: submitAnother },
  }
}

/**
 * Both buttons take the mutation's `isPending` as a REQUIRED `pending` prop
 * rather than leaving each form to write `disabled={submitting}` beside its
 * own label ternary. Double-submit protection is structural this way: a
 * form that wires the pair cannot forget it, and the two buttons cannot
 * disagree about whether a write is in flight. The primary label swaps to
 * `pendingLabel` ("Creating…") no matter which button fired — the pair
 * shares one submit handler, so one in-flight state is the truth for both.
 */
interface SubmitPairButtonProps extends ButtonProps {
  /** The mutation's `isPending`. Disables both buttons of the pair. */
  pending: boolean
}

/**
 * The primary (filled) submit button.
 *
 * These two are thin wrappers over `components/ui/button` — they exist
 * for the *names*, which say which half of the submit pair you are wiring,
 * not for styling. Note they used to compute `opacity-50 cursor-not-allowed`
 * from a `disabled === true` check written beside the `disabled` attribute;
 * `Button` derives it from the attribute with `disabled:` variants, so the
 * two can no longer disagree.
 *
 * No `role="status"` on the label: the create forms that use this pair
 * report success elsewhere (the page they navigate to, or the "and
 * another" path's own inline status line), and a second live region per
 * form makes every `getByRole('status')` ambiguous.
 */
export function PrimaryButton({
  pending,
  pendingLabel,
  disabled,
  children,
  variant = 'primary',
  ...props
}: SubmitPairButtonProps & {
  /** Label while the write is in flight, e.g. "Creating…". */
  pendingLabel: ReactNode
}): JSX.Element {
  return (
    <Button variant={variant} pending={pending} disabled={pending || disabled} {...props}>
      {pending ? (
        <>
          <PendingSpinner variant={variant} />
          {pendingLabel}
        </>
      ) : (
        children
      )}
    </Button>
  )
}

/** The secondary (outlined) submit button, for the "and another" variant. */
export function SecondaryButton({
  pending,
  disabled,
  ...props
}: SubmitPairButtonProps): JSX.Element {
  return <Button variant="secondary" disabled={pending || disabled} {...props} />
}
